import SPECS from './data/watch-specs.json';
import { fold } from './watchMatch';
import { Watch, WatchPatch } from './watchesApi';

/**
 * Bundled reference specs for well-known models.
 *
 * This is a lookup table compiled into the build, the same shape as
 * `brands.json` -- no request, no key, no server. A watch whose brand and model
 * match an entry gets its specs filled the moment it is added.
 *
 * The data is derived from model knowledge, not scraped from a manufacturer,
 * so it is stored with specs_source = 'seed' and shown as provisional until
 * the owner edits it. Coverage is deliberately partial: entries only exist for
 * references worth being confident about, and a miss simply leaves the fields
 * empty rather than guessing.
 *
 * Keyed by `fold(brand)|fold(model)` -- the same folding the news matcher uses,
 * so "A. Lange & Söhne" and "a lange sohne" resolve to one key.
 */
export type SpecFields = Pick<
  Watch,
  | 'case_material'
  | 'case_diameter'
  | 'movement'
  | 'power_reserve'
  | 'water_resistance'
  | 'crystal'
  | 'strap'
  | 'price_msrp'
  | 'year'
>;

const TABLE = SPECS as Record<string, Partial<SpecFields>>;

export const specKey = (brand: string, model: string) =>
  `${fold(brand)}|${fold(model)}`;

/** The bundled specs for a brand/model, or null when there is no entry. */
export function lookupSpecs(brand: string, model: string): Partial<SpecFields> | null {
  return TABLE[specKey(brand, model)] ?? null;
}

/**
 * The patch that fills a watch's empty spec fields from the bundled data.
 *
 * Only ever writes into blanks: a value the owner typed is never overwritten by
 * reference data, which is also why a watch already marked 'manual' is left
 * alone entirely. Returns null when there is nothing to fill, so callers can
 * skip the write.
 */
export function seedPatchFor(watch: Watch): WatchPatch | null {
  if (watch.specs_source === 'manual') return null;

  const found = lookupSpecs(watch.brand, watch.model);
  if (!found) return null;

  const patch: WatchPatch = {};
  for (const [key, value] of Object.entries(found)) {
    const field = key as keyof SpecFields;
    if (value != null && watch[field] == null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (patch as any)[field] = value;
    }
  }

  if (Object.keys(patch).length === 0) return null;
  patch.specs_source = 'seed';
  patch.specs_updated_at = new Date().toISOString();
  return patch;
}
